import { useState, useEffect, useRef, useCallback } from 'react';

type UsePullToRefreshOptions = {
  /** Element ID to attach the listener to. Defaults to 'page-content'. */
  targetId?: string;
  /** Minimum pull distance (px) to trigger refresh. Defaults to 80. */
  threshold?: number;
  /** Callback invoked when the user pulls to refresh. */
  onRefresh: () => Promise<void>;
  /** Whether pull-to-refresh is enabled. Defaults to true. */
  enabled?: boolean;
};

type UsePullToRefreshResult = {
  /** Whether a refresh is in progress. */
  isRefreshing: boolean;
  /** Current pull distance (0 when not pulling). Use for indicator animation. */
  pullDistance: number;
};

export default function usePullToRefresh({
  targetId = 'page-content',
  threshold = 80,
  onRefresh,
  enabled = true,
}: UsePullToRefreshOptions): UsePullToRefreshResult {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const pulling = useRef(false);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enabled || isRefreshing) return;
      const el = document.getElementById(targetId);
      if (!el || el.scrollTop > 0) return;
      startY.current = e.touches[0].clientY;
      pulling.current = true;
    },
    [enabled, isRefreshing, targetId]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!pulling.current) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta > 0) {
        // Apply resistance — pull distance is dampened
        const distance = Math.min(delta * 0.4, threshold * 1.5);
        setPullDistance(distance);
      } else {
        pulling.current = false;
        setPullDistance(0);
      }
    },
    [threshold]
  );

  const handleTouchEnd = useCallback(async () => {
    if (!pulling.current) return;
    pulling.current = false;

    if (pullDistance >= threshold) {
      setIsRefreshing(true);
      setPullDistance(0);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, threshold, onRefresh]);

  useEffect(() => {
    if (!enabled) return undefined;
    const el = document.getElementById(targetId);
    if (!el) return undefined;

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleTouchEnd);

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, targetId, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return { isRefreshing, pullDistance };
}
